import { useId, useRef, useState } from 'react';
import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import { Input } from '../input/index.ts';
import { ComboboxChip } from './combobox-chip.tsx';
import { ComboboxList } from './combobox-list.tsx';

export interface ComboboxProps {
  /** The values already picked, drawn as chips. Controlled — the caller owns them. */
  values: string[];
  onValuesChange: (values: string[]) => void;
  /** Every candidate there is. The filtering against what is typed happens here. */
  suggestions: string[];
  placeholder?: string;
  /** Put on the text field, so a `<label htmlFor>` names it. */
  id?: string;
  disabled?: boolean;
  /** Names one chip's remove button, e.g. `(name) => \`${name} verwijderen\``. */
  removeLabel?: (value: string) => string;
  className?: string;
}

/*
 * The key two values are compared on: trimmed and lowercased, and nothing else.
 * Accents are deliberately not folded away here — "José" and "Jose" are two
 * people, and adding the second must not be refused as a duplicate of the first.
 */
function sameKey(value: string): string {
  return value.trim().toLowerCase();
}

/*
 * The key the typing is matched against: the comparison key with its accents
 * stripped, so someone typing "jose" on a keyboard that makes accents hard
 * still finds "José".
 *
 * Matching is plain substring, never fuzzy: on a list of colleagues a fuzzy
 * match offers the wrong person about as often as the right one, and picking
 * the wrong person is a mistake that stays in the data.
 */
function searchKey(value: string): string {
  return sameKey(value)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * A field that holds several values: type, pick or invent, and each answer
 * becomes a chip you can take back off.
 *
 * Free text is first-class. The suggestions are a shortcut, not the set of
 * allowed answers — the first use is "who was this meeting with", where the
 * people already known are worth offering but a name typed for the first time
 * is the whole point. That is why nothing is highlighted until an arrow key
 * says so: Enter on freshly typed text adds exactly what was typed, and never
 * quietly swaps in the colleague whose name happens to contain it.
 *
 * One control rather than an input beside a list of chips: the chips live in
 * the same box as the cursor, so the field reads as "these, and what I am
 * typing" instead of as two things that happen to sit near each other.
 */
export function Combobox({
  values,
  onValuesChange,
  suggestions,
  placeholder,
  id,
  disabled = false,
  removeLabel,
  className,
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const taken = new Set(values.map(sameKey));
  const needle = searchKey(query);
  const matches = suggestions.filter(
    (suggestion) =>
      !taken.has(sameKey(suggestion)) && searchKey(suggestion).includes(needle),
  );

  /* An empty list is not a dropdown: there is nothing to show and nothing to arrow through. */
  const expanded = open && matches.length > 0 && !disabled;
  /*
   * The highlight is clamped rather than kept in sync by an effect: typing
   * shortens the list under it, and a stale index would light up the wrong row
   * for one render.
   */
  const highlighted = expanded && highlight < matches.length ? highlight : -1;

  function close() {
    setOpen(false);
    setHighlight(-1);
  }

  function commit(value: string) {
    const trimmed = value.trim();
    /*
     * A value already picked is not added twice, but the typing is still
     * cleared: the user asked for that person and that person is there, so
     * leaving their half-typed name behind would read as a failure.
     */
    if (trimmed !== '' && !taken.has(sameKey(trimmed))) {
      onValuesChange([...values, trimmed]);
    }
    setQuery('');
    close();
  }

  function add(value: string) {
    commit(value);
    inputRef.current?.focus();
  }

  function remove(index: number) {
    onValuesChange(values.filter((_, at) => at !== index));
    /* The button that was pressed has just been unmounted; put the cursor back where typing continues. */
    inputRef.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      /*
       * Always swallowed. This field lives inside forms and dialogs, where
       * Enter means "submit"; inside the field it means "add this one", and an
       * empty field means nothing at all — but never a submit the user did not
       * ask for while their attention is on a half-filled list.
       */
      event.preventDefault();
      const picked = highlighted >= 0 ? matches[highlighted] : undefined;
      if (picked !== undefined) {
        add(picked);
      } else if (query.trim() !== '') {
        add(query);
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (matches.length === 0) return;
      event.preventDefault();
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      /* From nothing highlighted, down lands on the first row and up on the last; both wrap. */
      const entry = step === 1 ? 0 : matches.length - 1;
      const next = (highlighted + step + matches.length) % matches.length;
      setHighlight(highlighted < 0 ? entry : next);
      return;
    }

    if (event.key === 'Escape') {
      /*
       * Only the list closes, and only if it is open. The dialog around this
       * field also listens for Escape, and dismissing the whole dialog because
       * someone waved away a suggestion list would lose everything typed.
       */
      if (expanded) {
        event.stopPropagation();
        close();
      }
      return;
    }

    if (event.key === 'Backspace' && query === '' && values.length > 0) {
      onValuesChange(values.slice(0, -1));
    }
  }

  /*
   * Focus leaving the whole control closes the list — clicking elsewhere on the
   * page, or tabbing on. Focus moving between the chips, the input and the rows
   * is still inside, so the list stays up.
   */
  function handleBlur(event: React.FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    /*
     * Whatever is still typed goes in as a value. A name left in the box is a
     * name the user meant: without this, clicking straight from the field to
     * the save button silently drops the person they typed. Without the
     * refocus of an explicit add — focus is leaving, and pulling it back
     * would trap the keyboard in the field.
     */
    commit(query);
  }

  return (
    <div data-slot='combobox' className={cn('relative', className)} onBlur={handleBlur}>
      <div
        data-slot='combobox-field'
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-md border border-transparent bg-fill-faint p-1.5',
          'transition focus-within:shadow-focus',
          disabled && 'opacity-40',
        )}
        /*
         * The whole box is the target: clicking the empty space beside the chips
         * is how anyone expects to start typing. preventDefault stops the click
         * from stealing the focus it is about to hand to the input.
         */
        onMouseDown={(event) => {
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {values.map((value, index) => (
          <ComboboxChip
            key={sameKey(value)}
            value={value}
            disabled={disabled}
            removeLabel={removeLabel}
            onRemove={() => remove(index)}
          />
        ))}

        <Input
          ref={inputRef}
          id={id}
          variant='bare'
          disabled={disabled}
          role='combobox'
          aria-expanded={expanded}
          aria-controls={expanded ? listboxId : undefined}
          aria-activedescendant={highlighted >= 0 ? optionId(highlighted) : undefined}
          aria-autocomplete='list'
          autoComplete='off'
          placeholder={placeholder}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            /* Every keystroke is a new list, so nothing carries a highlight into it. */
            setHighlight(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          /* Wide enough to type a name into, but happy to share a line with the chips. */
          className='min-w-32 flex-1 px-2 py-1'
        />
      </div>

      {expanded && (
        <ComboboxList
          id={listboxId}
          matches={matches}
          highlighted={highlighted}
          optionId={optionId}
          onHighlight={setHighlight}
          onPick={add}
        />
      )}
    </div>
  );
}
